import React, { useState } from 'react';
import { reportsApi, formatApiErrorDetail, useAuth } from '../../lib/api';
import { formatCurrency, formatDate, downloadCSV } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  FileText,
  Download,
  Loader2,
  BarChart3,
  Receipt,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';

export default function ReportsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState({});
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const handleExportWarrantySales = async () => {
    setLoading(prev => ({ ...prev, warranty: true }));
    try {
      const { data } = await reportsApi.warrantySales({
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      
      const exportData = data.map(sale => ({
        Date: formatDate(sale.sale_date),
        Branch: sale.branch_name,
        Staff: sale.staff_name,
        Customer: sale.customer_name,
        Phone: sale.customer_phone,
        Brand: sale.device_brand,
        Model: sale.device_model,
        IMEI: sale.device_imei,
        'Device Price': sale.device_price,
        Warranty: sale.warranty_attached ? 'Yes' : 'No',
        Certificate: sale.warranty_certificate || '-',
      }));
      
      downloadCSV(exportData, `warranty-sales-${dateRange.start}-to-${dateRange.end}`);
      toast.success('Report exported');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to generate report');
    } finally {
      setLoading(prev => ({ ...prev, warranty: false }));
    }
  };

  const handleExportRevenue = async () => {
    setLoading(prev => ({ ...prev, revenue: true }));
    try {
      const { data } = await reportsApi.revenue({
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      
      const exportData = data.map(item => ({
        Tenant: item.company_name,
        'Warranty Count': item.warranty_count,
        Revenue: item.revenue,
      }));
      
      downloadCSV(exportData, `revenue-report-${dateRange.start}-to-${dateRange.end}`);
      toast.success('Report exported');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to generate report');
    } finally {
      setLoading(prev => ({ ...prev, revenue: false }));
    }
  };

  const handleExportOutstanding = async () => {
    setLoading(prev => ({ ...prev, outstanding: true }));
    try {
      const { data } = await reportsApi.outstandingDues();
      
      const exportData = data.map(item => ({
        'Invoice #': item.invoice_number,
        Tenant: item.company_name,
        'Total Amount': item.total_amount,
        'Amount Paid': item.amount_paid,
        Outstanding: item.outstanding,
        Status: item.status,
      }));
      
      downloadCSV(exportData, `outstanding-dues-${formatDate(new Date())}`);
      toast.success('Report exported');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to generate report');
    } finally {
      setLoading(prev => ({ ...prev, outstanding: false }));
    }
  };

  const handleExportInvoiceSummary = async () => {
    setLoading(prev => ({ ...prev, invoices: true }));
    try {
      const { data } = await reportsApi.invoiceSummary({
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      const exportData = data.map(item => ({
        'Invoice #': item.invoice_number,
        Tenant: item.company_name,
        'Billing Period': item.billing_period,
        Subtotal: item.subtotal,
        Tax: item.tax_amount,
        'Total Amount': item.total_amount,
        'Amount Paid': item.amount_paid,
        Outstanding: item.outstanding,
        Status: item.status,
      }));
      downloadCSV(exportData, `invoice-summary-${dateRange.start}-to-${dateRange.end}`);
      toast.success('Report exported');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to generate report');
    } finally {
      setLoading(prev => ({ ...prev, invoices: false }));
    }
  };

  const reports = [
    {
      id: 'warranty',
      title: 'Warranty Sales Report',
      description: 'All sales with warranty status for the selected date range',
      icon: ShieldCheck,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      onClick: handleExportWarrantySales,
      available: true,
    },
    {
      id: 'invoices',
      title: 'Invoice Summary',
      description: 'All invoices with payment status for the selected period',
      icon: Receipt,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      onClick: handleExportInvoiceSummary,
      available: true,
    },
    {
      id: 'revenue',
      title: 'Revenue Report',
      description: 'Revenue per tenant for the selected period',
      icon: BarChart3,
      color: 'text-[#005DE8]',
      bgColor: 'bg-blue-50',
      onClick: handleExportRevenue,
      available: isSuperAdmin,
    },
    {
      id: 'outstanding',
      title: 'Outstanding Dues Report',
      description: 'Tenants with unpaid or overdue invoices',
      icon: Receipt,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      onClick: handleExportOutstanding,
      available: isSuperAdmin,
    },
  ];

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Reports
          </h1>
          <p className="text-slate-600 mt-1">Generate and export reports</p>
        </div>

        {/* Date Range */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Date Range</CardTitle>
            <CardDescription>Select the period for your reports</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full sm:w-48"
                  data-testid="report-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full sm:w-48"
                  data-testid="report-end-date"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.filter(r => r.available).map((report) => (
            <Card key={report.id} className="border-slate-200 card-hover">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 ${report.bgColor} rounded-xl flex items-center justify-center`}>
                    <report.icon className={`h-6 w-6 ${report.color}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{report.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{report.description}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full mt-4"
                  onClick={report.onClick}
                  disabled={loading[report.id]}
                  data-testid={`export-${report.id}`}
                >
                  {loading[report.id] ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info */}
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm text-slate-600">
                  Reports are generated as CSV files that can be opened in Excel, Google Sheets, or any spreadsheet application.
                  For PDF invoices, go to the Invoices section and download individual invoices.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
